import React from "react";
import { MapPin } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import useReveal from "../../hooks/useReveal";

const REGIONS = [
  { key: "cyprus", label: "Cyprus · Limassol", coords: "34.7071,33.0226" },
  { key: "turkey", label: "Turkey · Istanbul", coords: "41.0082,28.9784" },
  { key: "uae", label: "UAE · Dubai", coords: "25.2048,55.2708" },
];

const MapSection = () => {
  const { t } = useLanguage();
  const ref = useReveal();

  // Google Maps embed centered over Eastern Med / Gulf
  const embedUrl =
    "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d8891063.94!2d36.45!3d32.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2s!4v1700000000000";

  return (
    <section
      id="map"
      ref={ref}
      data-testid="map-section"
      className="relative py-24 md:py-32 bg-black"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <p
              className="reveal font-mono-l text-[11px] text-[#e8d4a8] mb-5 flex items-center gap-3"
              data-testid="map-eyebrow"
            >
              <span className="w-6 h-px bg-[#e8d4a8]"></span>
              {t.map.eyebrow}
            </p>
            <h2
              className="reveal reveal-delay-1 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
              data-testid="map-title"
            >
              {t.map.title}
            </h2>
          </div>
          <p className="reveal reveal-delay-2 text-white/60 max-w-md text-sm md:text-base font-light leading-relaxed">
            {t.map.subtitle}
          </p>
        </div>

        <div className="reveal relative border border-white/10 bg-[#0f0f0f] overflow-hidden">
          {/* Map embed */}
          <div className="relative aspect-[16/10] md:aspect-[21/9] w-full">
            <iframe
              title="Coverage map"
              src={embedUrl}
              className="absolute inset-0 w-full h-full"
              style={{
                border: 0,
                filter:
                  "grayscale(1) invert(0.92) contrast(0.9) hue-rotate(180deg) brightness(0.85)",
              }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="map-iframe"
            />
            {/* Gradient overlay for luxe feel */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20"></div>
          </div>

          {/* Region chips */}
          <div className="flex flex-wrap gap-3 p-6 md:p-8 border-t border-white/5 bg-black/60 backdrop-blur-xl">
            {REGIONS.map((r) => (
              <div
                key={r.key}
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#e8d4a8]/30 text-[#e8d4a8] font-mono-l text-[10px]"
                data-testid={`map-region-${r.key}`}
              >
                <MapPin size={12} strokeWidth={1.5} />
                {r.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MapSection;
